/**
 * What a publish from this page would send, given the lines left unticked (§5.25,
 * Claude Design kit 73).
 *
 * The draft stays what the person typed; the version is the published document
 * with the ticked lines taken from it (`pickChanges`), so a line left unticked is
 * still in the draft afterwards, and offered again. A secret's value goes with its
 * tick too, and one whose variable is left out cannot go: the Worker would refuse a
 * path the document does not name, so it is said here by name.
 *
 * A personal setting is never a line. An older document's are read as `null` on
 * both sides, and the next version drops them (1080/personal-settings-stay), which
 * `dropped` says, since nothing else on the page shows it.
 */
import type { SecretWrites } from '@airlock/contracts/cloud/wire';
import {
  describePath,
  groupChanges,
  looksOf,
  secretGroup,
  type ChangeGroup
} from '@airlock/contracts/workspace/change-groups';
import { diffDocuments, type RevisionDiff } from '@airlock/contracts/workspace/diff';
import type { WorkspaceDocument } from '@airlock/contracts/workspace/document';
import { parseWorkspaceDocument } from '@airlock/contracts/workspace/parse';
import { PERSONAL_CONFIG_KEYS, isPersonalKey, withoutPersonal } from '@airlock/contracts/workspace/personal';
import { pickChanges } from '@airlock/contracts/workspace/pick';
import { describeSecretPath, secretPathsOf } from '@airlock/contracts/workspace/secrets';

export interface PublishPlan {
  groups: ChangeGroup[];
  /** Every line's path, ticked or not. */
  lines: string[];
  /** Those a publish would send. */
  going: string[];
  /** Whether a line is left out, so the draft outlives the publish. */
  partial: boolean;
  /** The document to send. */
  document: WorkspaceDocument;
  /** The secret writes to send with it. */
  secrets: SecretWrites;
  /** The writes the page keeps afterwards. */
  left: SecretWrites;
  /** Why this choice cannot go, by name; empty when it can. */
  problems: string[];
  /** How many personal settings the published version still holds, and this one will not. */
  dropped: number;
}

export function planPublish({
  published,
  draft,
  writes,
  heldSecrets,
  held
}: {
  published: WorkspaceDocument;
  draft: WorkspaceDocument;
  writes: SecretWrites;
  /** The paths the account area already holds a value for. */
  heldSecrets: ReadonlySet<string>;
  /** The lines left unticked. */
  held: ReadonlySet<string>;
}): PublishPlan {
  const before = withoutPersonal(published);
  const after = withoutPersonal(draft);
  // A value typed for a secret the draft has since stopped naming is not offered,
  // except the removal of one the account area still holds.
  const allowed = (path: string, value: string | null, document: WorkspaceDocument): boolean =>
    secretPathsOf(document).has(path) || (value === null && heldSecrets.has(path));
  const offered = Object.entries(writes).filter(([path, value]) => allowed(path, value, after));

  const looks = looksOf(after.repos);
  const nameOf = (path: string): string => {
    const named = describeSecretPath(after, path);
    return named === path ? describeSecretPath(before, path) : named;
  };
  const secretLines = secretGroup(offered.map(([path]) => ({ path, label: nameOf(path) })));
  const groups = [...groupChanges(diffDocuments(before, after), looks), ...(secretLines ? [secretLines] : [])];

  const lines = groups.flatMap((group) => group.lines.map((line) => line.path));
  const going = lines.filter((path) => !held.has(path));
  const partial = going.length < lines.length;
  const document = partial ? withoutPersonal(pickChanges(before, after, new Set(going))) : after;

  const problems: string[] = [];
  if (partial) {
    const parsed = parseWorkspaceDocument(structuredClone(document));
    if (!parsed.ok) {
      for (const problem of parsed.problems) {
        problems.push(`${problemInWords(problem, after)} Tick what it depends on as well, or leave it out too.`);
      }
    }
  }
  const secrets: SecretWrites = {};
  for (const [path, value] of offered) {
    if (held.has(path)) continue;
    if (allowed(path, value, document)) secrets[path] = value;
    else problems.push(`The value of ${secretLines?.lines.find((line) => line.path === path)?.label ?? path} needs its variable ticked as well.`);
  }
  const left = Object.fromEntries(Object.entries(writes).filter(([path]) => !(path in secrets)));
  const said = published.config as unknown as Record<string, unknown>;
  const dropped = PERSONAL_CONFIG_KEYS.filter((key) => said[key] != null).length;

  return { groups, lines, going, partial, document, secrets, left, problems, dropped };
}

/** A version's diff as this page says it: what a person never decides is no line of it. */
export function teamDiff(diff: RevisionDiff): RevisionDiff {
  return diff.filter((entry) => !isPersonalKey(/^config\.(\w+)/.exec(entry.path)?.[1] ?? ''));
}

/** A parser's `repos[api].startCmd: …` in the words of the lines above it. */
export function problemInWords(problem: string, document: WorkspaceDocument): string {
  const split = problem.indexOf(': ');
  if (split <= 0) return problem;
  const path = problem.slice(0, split);
  if (!/^(repos|config|taskSources|name|color)\b/.test(path)) return problem;
  const named = path.replace(/^repos\[(\d+)\]/, (whole, at: string) => {
    const repo = document.repos[Number(at)];
    return repo?.name ? `repos[${repo.name}]` : whole;
  });
  return `${describePath(named, looksOf(document.repos))}: ${problem.slice(split + 2)}`;
}
